import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import {
    IconButton,
    Menu,
    MenuItem,
    Select,
} from '@material-ui/core'
import { MoreVert } from '@material-ui/icons'
import React, { useState } from 'react'
import { useClashPanelActionsStyles } from '../styles'
import { ClashFilterStatus } from '../types'

interface ClashPanelActionsProps {
    filterStatus?: ClashFilterStatus
    onFilterChange?: (status: ClashFilterStatus) => void
}

/**
 * Actions bar for clash panel including filter dropdown and more actions menu
 *
 * Related PRs: 177160, 177715
 */
export const ClashPanelActions: React.FC<ClashPanelActionsProps> = ({
    filterStatus = 'active',
    onFilterChange,
}) => {
    const classes = useClashPanelActionsStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const isOpen = Boolean(anchorEl)

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleResolveAll = () => {
        // Placeholder for future implementation
        handleClose()
    }

    const handleFilterChange = (
        event: React.ChangeEvent<{ value: unknown }>
    ) => {
        const newStatus = event.target.value as ClashFilterStatus
        onFilterChange?.(newStatus)
    }

    return (
        <GridItem
            data-testid="buttonGridItemBeta"
            className={classes.actionButtonGrid}
        >
            <GridContainer className={classes.toggleContainer}>
                <GridItem className={classes.actionsContainer}>
                    <Select
                        value={filterStatus}
                        onChange={handleFilterChange}
                        displayEmpty
                        variant="outlined"
                        className={classes.filterSelect}
                        classes={{
                            outlined: classes.selectOutlined,
                        }}
                        data-testid="clashFilterSelect"
                        aria-label="Clash Status Filter"
                        title="Clash Status Filter"
                        MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                            },
                            PaperProps: {
                                'aria-label': 'Clash Status Filter Options',
                            },
                        }}
                        renderValue={(selected) =>
                            selected === 'active' ? 'Active' : 'Resolved'
                        }
                    >
                        <MenuItem
                            value="active"
                            className={classes.filterOptionItem}
                            data-testid="filterOptionActive"
                            title="Active"
                            aria-label="Active"
                        >
                            Active
                        </MenuItem>
                        <MenuItem
                            value="resolved"
                            className={classes.filterOptionItem}
                            data-testid="filterOptionResolved"
                            title="Resolved"
                            aria-label="Resolved"
                        >
                            Resolved
                        </MenuItem>
                    </Select>
                </GridItem>
                <GridItem>
                    <IconButton
                        className={classes.moreIcon}
                        data-testid="moreActionButtonBeta"
                        title="More actions"
                        aria-haspopup="true"
                        aria-label="More actions button"
                        aria-expanded={isOpen}
                        onClick={handleOpen}
                        disableRipple
                        disableFocusRipple
                    >
                        <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={isOpen}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        getContentAnchorEl={null}
                    >
                        <MenuItem
                            onClick={handleResolveAll}
                            className={classes.menuItem}
                            data-testid="resolveAllMenuItem"
                        >
                            Resolve All
                        </MenuItem>
                    </Menu>
                </GridItem>
            </GridContainer>
        </GridItem>
    )
}
